/* Inventory sections: pill tabs, cached batches, "Show More" and the "Shop All" link.
   Collection tabs page through /collections/<handle>?view=sw-cards&page=N. Tabs with
   data-source pull their full list from window.SwInventorySources[source](tab, host).
   The pill bar is sticky in CSS only; nothing here listens to scroll or resize. */
window.SwInventorySources = window.SwInventorySources || {};

/* Images parsed by DOMParser belong to an inert document and their lazy-load state never
   starts. Rebuild each <img> in the live document and load it straight away: these cards
   only exist because the visitor asked for them or the section is about to scroll in. */
window.swInventoryRevive = function swInventoryRevive(root) {
  root.querySelectorAll('img').forEach((img) => {
    const fresh = document.createElement('img');
    Array.from(img.attributes).forEach((attr) => fresh.setAttribute(attr.name, attr.value));
    /* The hover photo stays lazy: it is display:none on touch screens, so it never downloads there. */
    const isHover = img.classList.contains('sw-product-card__image--hover');
    fresh.setAttribute('loading', isHover ? 'lazy' : 'eager');
    fresh.setAttribute('decoding', 'async');
    img.replaceWith(fresh);
  });
  return root;
};

/* Several sections can ask for the same collection page (the homepage has two "New Listings"
   grids). Share one request per URL for the life of the page. */
window.swInventoryPages = window.swInventoryPages || new Map();

if (!customElements.get('sw-inventory-tabs')) {
  const PAGE_SIZE = 50;

  class SwInventoryTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.perPage = Number(this.dataset.perPage) || 8;
      this.tabs = Array.from(this.querySelectorAll('[data-sw-tab]'));
      this.bar = this.querySelector('[data-sw-tabbar]');
      this.tablist = this.querySelector('[data-sw-tablist]');
      this.panel = this.querySelector('[data-sw-panel]');
      this.grid = this.querySelector('[data-sw-grid]');
      this.moreWrap = this.querySelector('[data-sw-more-wrap]');
      this.more = this.querySelector('[data-sw-more]');
      this.all = this.querySelector('[data-sw-all]');
      this.allLabel = this.querySelector('[data-sw-all-label]');
      this.skeleton = this.querySelector('template[data-sw-skeleton]');
      if (!this.grid || !this.tabs.length) return;

      this.cache = new Map();
      this.active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

      const cards = Array.from(this.grid.querySelectorAll('.sw-inv__cell:not(.sw-inv__skeleton)'));
      if (cards.length) {
        const entry = this.entry(this.active);
        entry.items = cards.map((cell) => cell.cloneNode(true));
        entry.shown = cards.length;
        if (!this.active.dataset.source && cards.length < this.perPage) entry.done = true;
      }

      this.tablist?.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-sw-tab]');
        if (tab && !tab.hidden) this.select(tab, true);
      });
      this.tablist?.addEventListener('keydown', (event) => this.onKey(event));
      this.more?.addEventListener('click', () => this.showMore());

      if (this.hasAttribute('data-lazy') && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.start();
          },
          { rootMargin: '0px 0px 800px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    start() {
      this.tabs
        .filter((tab) => tab.hasAttribute('data-preload'))
        .forEach((tab) => {
          this.ensure(tab, 1)
            .then(() => {
              if (this.entry(tab).items.length) tab.hidden = false;
              else this.hideTab(tab);
            })
            .catch(() => this.hideTab(tab));
        });

      if (this.entry(this.active).shown) {
        this.updateMore();
        this.warm(this.active, this.perPage * 2);
      } else {
        this.select(this.active, false);
      }
    }

    entry(tab) {
      const key = tab.dataset.key;
      if (!this.cache.has(key)) {
        this.cache.set(key, { items: [], pagesLoaded: 0, done: false, shown: 0, loading: null });
      }
      return this.cache.get(key);
    }

    visibleTabs() {
      return this.tabs.filter((tab) => !tab.hidden);
    }

    onKey(event) {
      const tabs = this.visibleTabs();
      let index = tabs.indexOf(event.target.closest('[data-sw-tab]'));
      if (index < 0) return;
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      tabs[index].focus({ preventScroll: true });
      this.select(tabs[index], false);
    }

    /* Horizontal only, so the page never jumps vertically. */
    revealPill(tab) {
      if (!this.tablist || this.bar?.hidden) return;
      const list = this.tablist.getBoundingClientRect();
      const pill = tab.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(this.tablist).scrollPaddingLeft) || 0;
      if (pill.left >= list.left + pad && pill.right <= list.right - pad) return;
      const left = this.tablist.scrollLeft + pill.left - list.left - (list.width - pill.width) / 2;
      this.tablist.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }

    /* If the section top has scrolled above the pinned bar, bring it back; otherwise stay put. */
    returnToTop() {
      const section = this.closest('.sw-section') || this;
      const offset = parseFloat(getComputedStyle(this.bar || this).top) || 0;
      const top = section.getBoundingClientRect().top;
      if (top >= offset) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + top - offset, behavior: reduce ? 'auto' : 'smooth' });
    }

    async select(tab, fromClick) {
      const changed = tab !== this.active;
      if (!changed && this.entry(tab).shown) return;

      this.tabs.forEach((item) => {
        const on = item === tab;
        item.setAttribute('aria-selected', on ? 'true' : 'false');
        item.tabIndex = on ? 0 : -1;
      });
      this.active = tab;
      if (this.panel && !this.bar?.hidden) this.panel.setAttribute('aria-labelledby', tab.id);
      this.revealPill(tab);
      this.updateAll(tab);
      if (fromClick && changed) this.returnToTop();

      const entry = this.entry(tab);
      entry.shown = 0;
      this.lockHeight();
      if (entry.items.length < this.perPage && !entry.done) {
        this.renderSkeleton();
        try {
          await this.ensure(tab, this.perPage);
        } catch (error) {
          /* render whatever is cached */
        }
      }
      if (this.active !== tab) return;

      if (!entry.items.length && tab.dataset.source) {
        this.unlockHeight();
        this.hideTab(tab);
        return;
      }
      this.render(tab, this.perPage, false);
      this.unlockHeight();
      this.warm(tab, this.perPage * 2);
    }

    hideTab(tab) {
      tab.hidden = true;
      const visible = this.visibleTabs();
      if (this.bar && visible.length < 2) {
        this.bar.hidden = true;
        this.panel?.setAttribute('aria-labelledby', this.closest('[aria-labelledby]')?.getAttribute('aria-labelledby') || '');
      }
      if (tab !== this.active) return;
      if (visible.length) this.select(visible[0], false);
      else (this.closest('.shopify-section') || this).setAttribute('hidden', '');
    }

    updateAll(tab) {
      if (!this.all) return;
      const url = tab.dataset.url;
      const label = tab.dataset.allLabel;
      if (url && label) {
        this.all.href = url;
        if (this.allLabel) this.allLabel.textContent = label;
        this.all.hidden = false;
      } else {
        this.all.hidden = true;
      }
    }

    async showMore() {
      const tab = this.active;
      const entry = this.entry(tab);
      const target = entry.shown + this.perPage;
      this.moreWrap?.classList.add('is-loading');
      this.more?.setAttribute('aria-busy', 'true');
      try {
        await this.ensure(tab, target);
      } catch (error) {
        /* keep what we have */
      }
      this.moreWrap?.classList.remove('is-loading');
      this.more?.removeAttribute('aria-busy');
      if (this.active !== tab) return;
      this.render(tab, target, true);
      this.warm(tab, target + this.perPage);
    }

    warm(tab, count) {
      this.ensure(tab, count)
        .catch(() => {})
        .then(() => {
          if (this.active === tab) this.updateMore();
        });
    }

    updateMore() {
      if (!this.moreWrap) return;
      const tab = this.active;
      const entry = this.entry(tab);
      const total = Number(tab.dataset.count) || 0;
      let more;
      if (entry.shown < entry.items.length) more = true;
      else if (entry.done) more = false;
      else more = total === 0 || entry.shown < total;
      this.moreWrap.hidden = !more;
    }

    /* Make sure at least `count` cards are cached for `tab`. */
    async ensure(tab, count) {
      const entry = this.entry(tab);
      while (entry.items.length < count && !entry.done) {
        if (entry.loading) {
          await entry.loading;
          continue;
        }
        entry.loading = this.load(tab, entry).finally(() => {
          entry.loading = null;
        });
        await entry.loading;
      }
    }

    async load(tab, entry) {
      const source = tab.dataset.source;
      if (source) {
        const provider = window.SwInventorySources[source];
        const items = provider ? await provider(tab, this) : [];
        this.add(tab, entry, items || []);
        entry.done = true;
        return;
      }
      const page = entry.pagesLoaded + 1;
      const items = await this.fetchPage(tab.dataset.url, page);
      entry.pagesLoaded = page;
      this.add(tab, entry, items);
      if (items.length < PAGE_SIZE) entry.done = true;
    }

    /* Dedupe by product link; page 1 overlaps the server-rendered cards. */
    add(tab, entry, items) {
      const exclude = tab.dataset.exclude ? `/products/${tab.dataset.exclude}` : null;
      const hrefOf = (cell) => {
        const href = cell.querySelector('a[href]')?.getAttribute('href') || '';
        return href.split('?')[0];
      };
      const seen = new Set(entry.items.map(hrefOf));
      items.forEach((item) => {
        const cell = this.toCell(item);
        const href = hrefOf(cell);
        if (!href || seen.has(href) || (exclude && href.endsWith(exclude))) return;
        seen.add(href);
        entry.items.push(cell);
      });
    }

    toCell(item) {
      if (item.classList?.contains('sw-inv__cell')) return item;
      const cell = document.createElement('li');
      cell.className = 'sw-inv__cell';
      cell.append(...Array.from(item.childNodes).map((node) => document.importNode(node, true)));
      return cell;
    }

    async fetchPage(url, page) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('view', 'sw-cards');
      target.searchParams.set('page', String(page));
      const key = target.toString();
      const pages = window.swInventoryPages;
      if (!pages.has(key)) {
        const request = fetch(target, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(`sw-cards ${response.status}`);
          return response.text();
        });
        request.catch(() => pages.delete(key));
        pages.set(key, request);
      }
      const html = await pages.get(key);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('li.sw-product-carousel__item'));
    }

    render(tab, count, append) {
      const entry = this.entry(tab);
      const from = append ? entry.shown : 0;
      const to = Math.min(count, entry.items.length);
      const fragment = document.createDocumentFragment();
      for (let i = from; i < to; i += 1) {
        const cell = window.swInventoryRevive(entry.items[i].cloneNode(true));
        cell.classList.add('sw-load-in');
        cell.style.animationDelay = `${Math.min(i - from, 7) * 40}ms`;
        fragment.append(cell);
      }
      const firstNew = fragment.firstElementChild;
      if (append) this.grid.querySelectorAll('.sw-inv__skeleton').forEach((node) => node.remove());
      else this.grid.replaceChildren();
      this.grid.append(fragment);
      this.grid.removeAttribute('aria-busy');
      entry.shown = to;
      this.updateMore();

      if (append && firstNew) {
        const link = firstNew.querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
      }
    }

    renderSkeleton() {
      if (!this.skeleton) return;
      const cell = this.skeleton.content.firstElementChild;
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < this.perPage; i += 1) fragment.append(document.importNode(cell, true));
      this.grid.replaceChildren(fragment);
      this.grid.setAttribute('aria-busy', 'true');
    }

    /* Hold the grid height while it swaps so the page does not collapse under the visitor. */
    lockHeight() {
      const height = this.grid.getBoundingClientRect().height;
      if (height > 0) this.grid.style.minHeight = `${height}px`;
    }

    unlockHeight() {
      requestAnimationFrame(() => {
        this.grid.style.minHeight = '';
      });
    }
  }

  customElements.define('sw-inventory-tabs', SwInventoryTabs);
}
